/**
 * The shop file: one merchant's whole picture, opened with a reason.
 *
 * JSX-free on purpose, like the rest of `src/lib`: the dashboard's Jest runs in `node` with no
 * jsdom, so the paths, the tab list and the grant bookkeeping are only testable out here.
 *
 * **Why a grant.** Opening a shop writes one `OperatorAuditLog` row with the operator's reason
 * (AVV §11.1, "Zugriff … nur anlassbezogen") and returns a grant; every read of that shop then
 * sends it as `X-Ops-Shop-Access`. The API refuses a read without one, so the reason prompt is not
 * a courtesy of this UI that a direct call could skip. The grant covers one shop for two hours, so
 * moving between the file's tabs does not ask again.
 */

/** The section root: the search. `/shops/:shopId` is one shop's file. */
export const SHOP_FILE_BASE = '/shops';

/** One tab of the shop file, keyed by its path segment under `/shops/:shopId`. `''` = overview. */
export type ShopFileTab =
  | ''
  | 'diagnosis'
  | 'documents'
  | 'email'
  | 'datev'
  | 'billing'
  | 'backfill'
  | 'period-backfill'
  | 'numbering';

/**
 * The tabs in display order.
 *
 * `backfill` (one order) and `numbering` live here rather than as sidebar tools of their own:
 * they act on one shop, an operator reaches for them while that shop's file is already open, and
 * a second sidebar entry that also said "shop" left nobody sure which to open. The one-order
 * backfill sits next to the period backfill — the two ways of closing an invoice gap.
 */
export const SHOP_FILE_TABS: ReadonlyArray<{
  readonly tab: ShopFileTab;
  readonly labelKey: string;
}> = [
  { tab: '', labelKey: 'shopFile.tabs.overview' },
  { tab: 'diagnosis', labelKey: 'shopFile.tabs.diagnosis' },
  { tab: 'documents', labelKey: 'shopFile.tabs.documents' },
  { tab: 'email', labelKey: 'shopFile.tabs.email' },
  { tab: 'datev', labelKey: 'shopFile.tabs.datev' },
  { tab: 'billing', labelKey: 'shopFile.tabs.billing' },
  { tab: 'backfill', labelKey: 'shopFile.tabs.backfill' },
  { tab: 'period-backfill', labelKey: 'shopFile.tabs.periodBackfill' },
  { tab: 'numbering', labelKey: 'shopFile.tabs.numbering' },
];

/**
 * The path of one shop's file, optionally a tab of it, optionally carrying the ticket the agent
 * came from — the ticket is what lets the reason prompt answer itself.
 */
export function shopFilePath(
  shopId: string,
  tab: ShopFileTab = '',
  ticketId?: string,
): string {
  const base = `${SHOP_FILE_BASE}/${encodeURIComponent(shopId)}`;
  const path = tab ? `${base}/${tab}` : base;
  return ticketId ? `${path}?ticket=${encodeURIComponent(ticketId)}` : path;
}

/** The three things the overview can say about a shop's Shopify grant. */
export type ShopifyScopesStatus = 'approved' | 'missing' | 'unknown';

/** The slice of the file's `shopifyScopes` section the status reads. */
export interface ShopifyScopesFacts {
  readonly grantKnown: boolean;
  readonly missingScopes: readonly string[];
}

/**
 * Which badge the overview draws for the shop's Shopify permissions (`read_customers`,
 * `read_products` — the scopes the REST→GraphQL migration added).
 *
 * `unknown` is kept apart from `missing` on purpose: a shop with no stored offline session is an
 * uninstall whose row survived or an install that never completed, not a merchant who has to
 * act. Collapsing the two would send support after a merchant who cannot open the app.
 *
 * @param facts - The file's `shopifyScopes` section.
 */
export function shopifyScopesStatus(
  facts: ShopifyScopesFacts,
): ShopifyScopesStatus {
  if (!facts.grantKnown) return 'unknown';
  return facts.missingScopes.length === 0 ? 'approved' : 'missing';
}

/**
 * The catalogue key of the badge for a status. Full literals, so the catalogue parity spec sees
 * each one.
 *
 * @param status - What {@link shopifyScopesStatus} decided.
 */
export function shopifyScopesBadgeKey(status: ShopifyScopesStatus): string {
  if (status === 'approved') return 'shopFile.overview.scopesApproved';
  if (status === 'missing') return 'shopFile.overview.scopesMissing';
  return 'shopFile.overview.scopesUnknown';
}

/** A Shopify order ID as the backfill takes it — digits only. */
const NUMERIC_ORDER_ID = /^\d+$/;

/**
 * The one-order backfill tab, prefilled with the order. Only prefilled: the reason and the
 * confirmation stay the operator's. The ticket rides along like on every other shop-file link,
 * so the reason prompt can answer itself.
 */
export function shopFileBackfillPath(
  shopId: string,
  orderId: string,
  ticketId?: string,
): string {
  const [path = '', query = ''] = shopFilePath(
    shopId,
    'backfill',
    ticketId,
  ).split('?');
  const params = new URLSearchParams(query);
  params.set('order', orderId);
  return `${path}?${params.toString()}`;
}

/**
 * The order the backfill tab is prefilled with, from its query string — `''` when there is none
 * or it is malformed. Dropped rather than shown: the form's own validation is the gate, and a
 * prefill that fails it would read as a typo.
 */
export function readBackfillOrder(search: URLSearchParams): string {
  const order = search.get('order') ?? '';
  return NUMERIC_ORDER_ID.test(order) ? order : '';
}

/** A grant as the dashboard keeps it. */
export interface StoredShopGrant {
  readonly grant: string;
  readonly expiresAt: string;
  readonly auditLogId: string;
  readonly shopDomain: string;
}

/** The slice of the Storage API the grant store needs — so a spec can hand in a Map. */
export interface GrantStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Where grants live. `sessionStorage`, not `localStorage`: a reason is given for THIS visit, and
 * a grant surviving into tomorrow's browser session would read as access nobody asked for.
 */
const GRANT_KEY_PREFIX = 'ops-shop-grant:';

/** Whether a parsed value has the stored grant's shape. Narrowed, not asserted. */
function isStoredGrant(value: unknown): value is StoredShopGrant {
  if (typeof value !== 'object' || value === null) return false;
  const record: Record<string, unknown> = { ...value };
  return (
    typeof record['grant'] === 'string' &&
    typeof record['expiresAt'] === 'string' &&
    typeof record['auditLogId'] === 'string' &&
    typeof record['shopDomain'] === 'string'
  );
}

/**
 * The stored grant for a shop, if it is still valid.
 *
 * Treated as expired a minute early, so a request never leaves with a grant that lapses on the
 * way to the API and comes back as a confusing reason prompt mid-click.
 */
export function readShopGrant(
  storage: GrantStorage,
  shopId: string,
  now: Date = new Date(),
): StoredShopGrant | null {
  const raw = storage.getItem(`${GRANT_KEY_PREFIX}${shopId}`);
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    storage.removeItem(`${GRANT_KEY_PREFIX}${shopId}`);
    return null;
  }
  if (!isStoredGrant(parsed)) return null;
  if (new Date(parsed.expiresAt).getTime() - 60_000 <= now.getTime()) {
    storage.removeItem(`${GRANT_KEY_PREFIX}${shopId}`);
    return null;
  }
  return parsed;
}

/** Stores a grant for a shop. */
export function writeShopGrant(
  storage: GrantStorage,
  shopId: string,
  grant: StoredShopGrant,
): void {
  storage.setItem(`${GRANT_KEY_PREFIX}${shopId}`, JSON.stringify(grant));
}

/** Drops a shop's grant — after the API refused it, so the next read prompts again. */
export function forgetShopGrant(storage: GrantStorage, shopId: string): void {
  storage.removeItem(`${GRANT_KEY_PREFIX}${shopId}`);
}

/** The API's refusal message for a read without a valid grant (`shop-access.ts`). */
export const SHOP_ACCESS_REQUIRED = 'SHOP_ACCESS_REQUIRED';

/** Minimum length of a typed reason — the API's `@MinLength(10)`. */
export const REASON_MIN_LENGTH = 10;

/** Whether a typed reason is long enough for the API to take it. */
export function isReasonAcceptable(reason: string): boolean {
  return reason.trim().length >= REASON_MIN_LENGTH;
}
